import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap, useGSAP } from '@/lib/gsap-config';
import { Type } from 'lucide-react';
import { StudioSequence } from './StudioSequence';
import { LayoutCinema } from './LayoutCinema';

export function MorphTypographySection() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const containerRef = useRef<HTMLDivElement>(null);
  const typoWorkbenchRef = useRef<HTMLDivElement>(null);
  const typoBoxRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const specimenGlyphRef = useRef<SVGPathElement>(null);
  const laserTraceRef = useRef<SVGPathElement>(null);
  const opticalProbeRef = useRef<SVGGElement>(null);
  const probeCoordTextRef = useRef<SVGTextElement>(null);
  const caliperStemLine2Ref = useRef<SVGLineElement>(null);
  const stemWidthTextRef = useRef<SVGTextElement>(null);
  const weightDisplayRef = useRef<HTMLSpanElement>(null);
  const viewingRangeRef = useRef<HTMLSpanElement>(null);

  // MorphSVG Specimen Letterform "A" (Hairline Vector Skeleton -> Heavy Auditorium Black)
  const shapeHairline = 'M 96,25 L 104,25 L 168,175 L 158,175 L 138,128 L 62,128 L 42,175 L 32,175 Z M 68,114 L 132,114 L 100,38 Z';
  const shapeBlack = 'M 76,16 L 124,16 L 194,175 L 150,175 L 140,136 L 60,136 L 50,175 L 6,175 Z M 64,122 L 136,122 L 100,28 Z';

  // Variable font dynamic weight scrub with MorphSVG, DrawSVG & MotionPath Calipers
  useGSAP(() => {
    const headline = headlineRef.current;
    if (!headline || !typoWorkbenchRef.current) return;
    const media = gsap.matchMedia();
    media.add({ motion: '(prefers-reduced-motion: no-preference)', desktop: '(min-width: 1000px) and (min-height: 850px)' }, (context) => {
    if (!context.conditions?.motion) return;
    const desktop = !!context.conditions.desktop;

    const typoTl = gsap.timeline({
      scrollTrigger: {
        trigger: typoWorkbenchRef.current,
        start: desktop ? 'top 90px' : 'top 75%',
        end: desktop ? '+=1000' : 'bottom 30%',
        pin: desktop,
        pinSpacing: true,
        anticipatePin: 1,
        scrub: 1,
        invalidateOnRefresh: true,
      },
    });

    typoTl.fromTo(typoBoxRef.current, { clipPath: 'inset(0% 8% 0% 8% round 24px)' }, { clipPath: 'inset(0% 0% 0% 0% round 12px)', duration: 0.7 }, 0);
    const weightObj = { wght: 200 };

    // 1. DrawSVG: Caliper lines, bounding frame, measurement ticks
    typoTl
      .fromTo(
        '.typo-caliper-frame',
        { drawSVG: '0% 0%' },
        { drawSVG: '0% 100%', duration: 0.5, ease: 'power2.out' },
        0
      )
      .fromTo(
        '.typo-caliper-line',
        { drawSVG: '0% 0%' },
        { drawSVG: '0% 100%', duration: 0.6, stagger: 0.04, ease: 'power2.out' },
        0.05
      )
      .fromTo(
        '.typo-caliper-tick',
        { drawSVG: '0% 0%' },
        { drawSVG: '0% 100%', duration: 0.5, stagger: 0.015, ease: 'power2.out' },
        0.1
      )
      // 2. MotionPath: AI Optical Laser Stylus traces the letterform contour trajectory
      .fromTo(
        opticalProbeRef.current,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.2 },
        0.05
      )
      .to(
        opticalProbeRef.current,
        {
          motionPath: {
            path: laserTraceRef.current!,
            align: laserTraceRef.current!,
            alignOrigin: [0.5, 0.5],
          },
          duration: 1.6,
          ease: 'power1.inOut',
        },
        0.1
      )
      // 3. DrawSVG: Luminous Laser Beam path drawing in real time ahead of the probe
      .fromTo(
        laserTraceRef.current,
        { drawSVG: '0% 0%' },
        { drawSVG: '0% 100%', duration: 1.6, ease: 'power1.inOut' },
        0.1
      )
      // 4. MorphSVG: Prominent Specimen Letterform "A" morphs from Hairline to Auditorium Black
      .to(
        specimenGlyphRef.current,
        {
          morphSVG: shapeBlack,
          duration: 1.6,
          ease: 'power1.inOut',
        },
        0.1
      )
      // 5. Caliper Stem Line physically widens
      .to(
        caliperStemLine2Ref.current,
        {
          x: 24,
          duration: 1.6,
          ease: 'power1.inOut',
        },
        0.1
      )
      // 6. Continuous Variable Font Weight Scrub (200 -> 800) with automatic kerning (0 React re-renders)
      .to(
        weightObj,
        {
          wght: 800,
          duration: 1.6,
          ease: 'power1.inOut',
          onUpdate: () => {
            const rounded = Math.round(weightObj.wght);
            headline.style.fontWeight = String(rounded);
            headline.style.fontVariationSettings = `'wght' ${rounded}`;
            headline.style.letterSpacing = `${((800 - rounded) / 600) * 0.02 - 0.02}em`;

            if (weightDisplayRef.current) {
              weightDisplayRef.current.textContent = `${rounded}`;
            }
            if (probeCoordTextRef.current) {
              probeCoordTextRef.current.textContent = `PROBE // ${rounded}`;
            }
            if (stemWidthTextRef.current) {
              const stemMm = (2.4 + ((rounded - 200) / 600) * 16.2).toFixed(1);
              stemWidthTextRef.current.textContent = `STEM // ${stemMm} mm`;
            }
            if (viewingRangeRef.current) {
              const distM = Math.round(5 + ((rounded - 200) / 600) * 30);
              viewingRangeRef.current.textContent = `${distM}m`;
            }
          },
        },
        0.1
      );

    return () => {
      headline.style.fontWeight = '200';
      headline.style.fontVariationSettings = "'wght' 200";
      headline.style.letterSpacing = '';
      if (weightDisplayRef.current) weightDisplayRef.current.textContent = '200';
      if (viewingRangeRef.current) viewingRangeRef.current.textContent = '5m';
      if (probeCoordTextRef.current) probeCoordTextRef.current.textContent = 'PROBE // 200';
      if (stemWidthTextRef.current) stemWidthTextRef.current.textContent = 'STEM // 2.4 mm';
    };
    });
    return () => media.revert();
  }, { scope: containerRef, dependencies: [isEn], revertOnUpdate: true });

  return (
    <section
      ref={containerRef}
      className="py-24 md:py-36 bg-background border-b border-border/80 relative overflow-hidden"
    >
      <div className="container mx-auto px-4 max-w-6xl">
        <LayoutCinema />

        {/* Variable Font Dynamic Morphing Showcase with Architectural Typographic Calipers */}
        <div ref={typoWorkbenchRef} className="type-studio pt-20 border-t border-border/60 max-w-5xl mx-auto">
          <header className="type-heading">
            <h2>{t('morph.fontTitle')}</h2>
            <p>{t('morph.fontIntro')}</p>
          </header>
          {/* Top HUD Control Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 font-mono text-xs text-muted-foreground uppercase tracking-widest pb-4 mb-6 border-b border-border/60">
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-brand" />
              <span className="text-foreground font-bold">[{t('morph.fontTag')}]</span>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              {/* Viewing Distance Badge */}
              <div className="flex items-center gap-1.5 px-3 py-1 rounded bg-muted/70 border border-border">
                <span className="text-[10px] text-muted-foreground">{t('morph.fontDistance')}</span>
                <span ref={viewingRangeRef} className="text-[11px] font-bold text-brand">5m</span>
              </div>

              {/* Live Weight Badge */}
              <div className="flex items-center gap-1.5 px-3 py-1 rounded bg-muted/70 border border-border">
                <span className="text-[10px] text-muted-foreground">{t('morph.fontWeight')}</span>
                <span ref={weightDisplayRef} className="text-[11px] font-bold text-foreground">200</span>
              </div>
            </div>
          </div>

          <div
            ref={typoBoxRef}
            className="type-workbench relative p-6 sm:p-10 select-none my-4 border border-border/80 rounded-card overflow-hidden bg-card/40 shadow-elevated"
          >
            <StudioSequence variant="type" />
            {/* Upper Architectural Studio: Vector Glyph Specimen, Calipers, MotionPath & DrawSVG */}
            <div className="relative w-full aspect-[21/9] min-h-[220px] max-h-[300px] mb-8 rounded-lg border border-border/60 bg-background/50 overflow-hidden flex items-center justify-center">
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none select-none"
                viewBox="0 0 840 240"
                preserveAspectRatio="xMidYMid meet"
                fill="none"
              >
                {/* Background Millimeter Grid */}
                <defs>
                  <pattern id="typo-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-border/30" />
                  </pattern>
                </defs>
                <rect width="840" height="240" fill="url(#typo-grid)" />

                {/* Outer Caliper Frame (DrawSVG) */}
                <rect
                  x="20"
                  y="15"
                  width="800"
                  height="210"
                  rx="6"
                  className="typo-caliper-frame stroke-border/70"
                  strokeWidth="1"
                  strokeDasharray="6 4"
                />

                {/* Ascender Guide Line (+800 EM) */}
                <line x1="20" y1="40" x2="820" y2="40" className="typo-caliper-line stroke-muted-foreground/30" strokeWidth="1" strokeDasharray="4 4" />
                <text x="30" y="34" className="fill-muted-foreground/60 font-mono text-[9px] uppercase">ASCENDER // +800 EM</text>

                {/* Cap-Height Guide Line (+680 EM) */}
                <line x1="20" y1="70" x2="820" y2="70" className="typo-caliper-line stroke-brand/40" strokeWidth="1.2" />
                <text x="810" y="64" textAnchor="end" className="fill-brand/70 font-mono text-[9px] uppercase">CAP-HEIGHT // +680 EM</text>

                {/* Midline X-Height (+500 EM) */}
                <line x1="20" y1="130" x2="820" y2="130" className="typo-caliper-line stroke-muted-foreground/20" strokeWidth="0.8" strokeDasharray="3 3" />
                <text x="30" y="124" className="fill-muted-foreground/50 font-mono text-[9px] uppercase">X-HEIGHT // +500 EM</text>

                {/* Baseline Datum (0.00 PT) */}
                <line x1="20" y1="195" x2="820" y2="195" className="typo-caliper-line stroke-brand/80" strokeWidth="1.5" />
                <text x="30" y="189" className="fill-brand font-mono text-[9px] font-bold uppercase">BASELINE // 0.00 PT (AUDITORIUM FOCUS)</text>

                {/* Descender Guide Line (-220 EM) */}
                <line x1="20" y1="220" x2="820" y2="220" className="typo-caliper-line stroke-muted-foreground/30" strokeWidth="1" strokeDasharray="4 4" />
                <text x="810" y="215" textAnchor="end" className="fill-muted-foreground/60 font-mono text-[9px] uppercase">DESCENDER // -220 EM</text>

                {/* Baseline Ruler Caliper Ticks (DrawSVG) */}
                {Array.from({ length: 27 }).map((_, i) => (
                  <line
                    key={i}
                    x1={40 + i * 29}
                    y1={191}
                    x2={40 + i * 29}
                    y2={199}
                    className="typo-caliper-tick stroke-brand/40"
                    strokeWidth="1"
                  />
                ))}

                {/* Center Vector Specimen Letterform "A" (MorphSVGPlugin) */}
                <g transform="translate(320, 20)">
                  <path
                    ref={specimenGlyphRef}
                    d={shapeHairline}
                    className="fill-brand/20 stroke-brand"
                    strokeWidth="1.5"
                  />
                </g>

                {/* Dynamic Stem Width Measurement Calipers */}
                <g transform="translate(130, 80)">
                  <line x1="0" y1="0" x2="0" y2="80" className="typo-caliper-line stroke-brand/50" strokeWidth="1.2" />
                  <line ref={caliperStemLine2Ref} x1="16" y1="0" x2="16" y2="80" className="typo-caliper-line stroke-brand/50" strokeWidth="1.2" />
                  <line x1="0" y1="40" x2="16" y2="40" className="typo-caliper-line stroke-brand" strokeWidth="1" />
                  <circle cx="0" cy="40" r="2.5" className="fill-brand" />
                  <circle cx="16" cy="40" r="2.5" className="fill-brand" />
                  <text ref={stemWidthTextRef} x="0" y="-10" className="fill-brand font-mono text-[10px] font-bold">
                    STEM // 2.4 mm
                  </text>
                  <text x="0" y="96" className="fill-muted-foreground/60 font-mono text-[8px] uppercase">
                    OPTICAL THICKNESS
                  </text>
                </g>

                {/* Right Side Dimension: Projected Auditorium Verification */}
                <g transform="translate(680, 80)">
                  <rect x="-10" y="-14" width="125" height="42" rx="4" className="fill-card/80 stroke-border" strokeWidth="1" />
                  <text x="0" y="4" className="fill-muted-foreground font-mono text-[9px] uppercase">OPTICAL CONTRAST</text>
                  <text x="0" y="20" className="fill-emerald-500 font-mono text-[11px] font-bold">14.2:1 PASSED</text>
                  <text x="0" y="42" className="fill-muted-foreground/60 font-mono text-[8px] uppercase">
                    WCAG 2.1 AAA AUDITORIUM
                  </text>
                </g>

                {/* Laser Scanning Trajectory (DrawSVG) */}
                <path
                  id="typo-probe-trajectory"
                  ref={laserTraceRef}
                  d="M 352,195 L 420,45 L 488,195 L 458,148 L 382,148 L 362,195"
                  className="stroke-brand/70"
                  strokeWidth="2"
                  strokeDasharray="6 3"
                  fill="none"
                />

                {/* AI Optical Laser Probe Reticle & Live Dynamic Reading (MotionPathPlugin) */}
                <g ref={opticalProbeRef} className="pointer-events-none">
                  <circle r="14" className="fill-brand/20 animate-ping" />
                  <circle r="7" className="fill-brand/40" />
                  <circle r="3" className="fill-brand" />
                  <line x1="-14" y1="0" x2="14" y2="0" stroke="currentColor" strokeWidth="1" className="text-brand" />
                  <line x1="0" y1="-14" x2="0" y2="14" stroke="currentColor" strokeWidth="1" className="text-brand" />
                  <rect x="14" y="-24" width="130" height="22" rx="4" className="fill-card/95 stroke-brand/50" strokeWidth="1" />
                  <text ref={probeCoordTextRef} x="22" y="-9" className="fill-brand font-mono text-[10px] font-bold">
                    PROBE // 200
                  </text>
                </g>
              </svg>
            </div>

            {/* Live Headline Preview Responding Directly to Variable Font Weight */}
            <div className="text-center pt-2">
              <h2
                ref={headlineRef}
                className="relative z-10 text-3xl sm:text-5xl md:text-6xl tracking-tight text-foreground leading-tight py-2 transition-[letter-spacing]"
                style={{ fontWeight: 200, fontVariationSettings: "'wght' 200" }}
              >
                {t('morph.fontHeadline')}
              </h2>

              <p className="relative z-10 font-mono text-xs sm:text-sm text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
                {t('morph.fontDesc')}
              </p>
            </div>
          </div>
        </div>


      </div>
    </section>
  );
}

export default MorphTypographySection;

